import type { AutocompleteProps } from '@mfw/components';

import { Autocomplete, get } from '@mfw/components';
import { type FieldError, Controller } from 'react-hook-form';

export interface FormAutocompleteProps extends Omit<
  AutocompleteProps,
  'errors'
> {
  name: string;
}

export const FormAutocomplete = ({
  addNewLabel,
  addNewLabelHelpText,
  defaultValue,
  name,
  onSelected,
  options,
  readOnly,
  title,
  total,
  withAddNew,
  ...otherProps
}: FormAutocompleteProps) => {
  return (
    <Controller
      render={({
        field,
        fieldState,
        formState: { defaultValues, errors, isLoading },
      }) => {
        // `Autocomplete.defaultValue` seeds its internal selection, so for a
        // multi-select the live form value has to win — otherwise a selection
        // made before the options finished loading is dropped when they
        // arrive. An EMPTY array only counts as a selection once the user has
        // touched the field: on a pristine field it just means the async
        // defaults have not resolved yet, but on a dirty one it is a
        // deliberate "clear" — falling back to the mount defaults there would
        // resurrect the removed chips visually while the form still holds [].
        const fieldValueSelection =
          Array.isArray(field.value) &&
          (field.value.length > 0 || fieldState.isDirty)
            ? field.value
            : undefined;
        const formDefaultValue =
          fieldValueSelection ??
          get(defaultValues, name) ??
          field.value ??
          undefined;
        const error = get<FieldError>(errors, name) ?? undefined;
        return (
          <Autocomplete
            onSelected={(value) => {
              field.onChange(value);
              onSelected?.(value);
            }}
            defaultValue={defaultValue ?? formDefaultValue}
            addNewLabelHelpText={addNewLabelHelpText}
            addNewLabel={addNewLabel}
            withAddNew={withAddNew}
            errors={error?.message}
            readOnly={readOnly}
            options={options}
            title={title}
            {...field}
            total={total}
            {...otherProps}
            loading={otherProps?.loading || isLoading}
          />
        );
      }}
      name={name}
    />
  );
};
